import React, { useState, useEffect } from 'react';
import DropdownMenu from './DropdownMenu';
import MultiSelectDropdown from './MultiSelectDropdown';
import StyleDropdown from './StyleDropdown';
import SuggestionsMenu from './SuggestionsMenu';
import FileUpload from './FileUpload';
import MobileFilterModal from './MobileFilterModal';
import OutOfCreditsModal from './OutOfCreditsModal';
import { FileProcessor } from '../utils/fileProcessor';
import { RateLimitStatus as RateLimitStatusType, StyleState, FilterState } from '../types/api';
import { useOutOfCreditsModal } from '../hooks/useOutOfCreditsModal';
import { mixpanelService } from '../services/mixpanelService';
import { MixpanelScreenClickButton } from '../types/mixpanel';
import { stringToHairArray } from '../utils/filterUtils';

import { ASSETS } from '../assets/assetMapping';

const img = ASSETS.PLUS_ICON; // Plus icon (for attachment/file upload)
const img1 = ASSETS.MAGIC_WAND_ICON; // Magic wand icon (for suggestions)  
const img2 = ASSETS.ARROW_ICON; // Arrow icon

interface EnhancedInputAvatarProps {
  onPromptChange: (prompt: string) => void;
  onFiltersChange: (filters: FilterState) => void;
  onFileChange?: (file: File | null, base64: string | null) => void;
  initialPrompt?: string;
  initialFilters?: FilterState;
  onGenerate?: () => void;
  isGenerating?: boolean;
  rateLimitStatus?: RateLimitStatusType | null;
  disabled?: boolean;
  // History props
  hasHistory?: boolean;
  onHistoryToggle?: () => void;
  isHistoryVisible?: boolean;
  isMobileView?: boolean;
  // Tracking props
  hasResults?: boolean;
}

const dropdownOptions = {
  age: [
    { value: 'under20', label: '<20' },
    { value: '20s', label: '20s' },
    { value: '30s', label: '30s' },
    { value: '40s', label: '40s' },
    { value: '50s', label: '50s' },
    { value: '60plus', label: '60+' },
    { value: 'any', label: 'Any' }
  ],
  gender: [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'any', label: 'Any' }
  ],
  hair: [
    { value: 'blonde', label: 'Blonde' },
    { value: 'brunette', label: 'Brunette' },
    { value: 'black', label: 'Black hair' },
    { value: 'curly', label: 'Curly' },
    { value: 'straight', label: 'Straight' },
    { value: 'grey', label: 'Grey' },
    { value: 'pink', label: 'Pink' },
    { value: 'any', label: 'Any' }
  ],
  background: [
    { value: 'Green Screen', label: 'Green Screen' },
    { value: 'Office Desk', label: 'Office Desk' },
    { value: 'Interview setup with mic', label: 'Interview setup with mic' },
    { value: 'Car interior', label: 'Car interior' },
    { value: 'Holding laptop', label: 'Holding laptop' },
    { value: 'Coffee shop', label: 'Coffee shop' },
    { value: 'Home Office', label: 'Home Office' },
    { value: 'any', label: 'Any' }
  ],
  model: [
    { value: 'flux-dev', label: 'FLUX Dev' },
    { value: 'seedream-4', label: 'Seedream 4.0' },
    { value: 'nano-banana', label: 'Nano Banana' }
  ]
};


export default function EnhancedInputAvatar({ 
  onPromptChange, 
  onFiltersChange, 
  onFileChange,
  initialPrompt = '',
  initialFilters,
  onGenerate,
  isGenerating = false,
  rateLimitStatus,
  disabled = false,
  hasHistory = false,
  onHistoryToggle,
  isHistoryVisible = false,
  isMobileView = false,
  hasResults = false
}: EnhancedInputAvatarProps) {
  const [prompt, setPrompt] = useState(initialPrompt);
  
  // Out of credits modal
  const { isOpen: isOutOfCreditsOpen, openModal: openOutOfCreditsModal, closeModal: closeOutOfCreditsModal, handleBuyCredits } = useOutOfCreditsModal();

  //Update prompt when initialPrompt changes (like when avatar card is clicked)
  useEffect(() => {
    setPrompt(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    if (initialFilters) {
      // Convert hair from string to array if needed (for backward compatibility)
      const convertedFilters = {
        ...initialFilters,
        hair: stringToHairArray(initialFilters.hair)
      };
      setFilters(convertedFilters);
    }
  }, [initialFilters]);

  const [filters, setFilters] = useState<FilterState>({
    style: {
      type: 'photorealistic',
      ratio: 'vertical',
      scene: 'mid-closeup'
    },
    age: 'any',
    gender: 'any',
    hair: [],
    background: 'any',
    model: 'flux-dev'
  });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Unused - kept for future use
  // const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        FileProcessor.revokePreviewUrl(previewUrl);
      }
    };
  }, [previewUrl]);

  // Helper function to close all menus
  const closeAllMenus = () => {
    setShowSuggestions(false);
    setShowFileUpload(false);
    setOpenDropdown(null);
    if (isHistoryVisible && onHistoryToggle) {
      onHistoryToggle();
    }
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newPrompt = e.target.value;
    setPrompt(newPrompt);
    onPromptChange(newPrompt);
  };

  // Helper to normalize filter values for button names
  const normalizeValueForButton = (filterType: string, value: string): string => {
    // Normalize model values
    if (filterType === 'model') {
      const modelMap: Record<string, string> = {
        'flux-dev': 'flux',
        'seedream-4': 'seedream',
        'nano-banana': 'nano-banana',
      };
      return modelMap[value] || value;
    }
    
    // Normalize age values
    if (filterType === 'age') {
      const ageMap: Record<string, string> = {
        'under20': '<20',
        '20s': '20s',
        '30s': '30s',
        '40s': '40s',
        '50s': '50s',
        '60s': '60s',
        '60plus': '60+',
        'any': 'any',
      };
      return ageMap[value] || value;
    }
    
    // Normalize scene values
    if (filterType === 'scene') {
      const sceneMap: Record<string, string> = {
        'mid-closeup': 'mid-shot',
        'headshot': 'headshot',
        'full-body': 'full-body',
      };
      return sceneMap[value] || value;
    }
    
    // Normalize background values
    if (filterType === 'background') {
      const bgMap: Record<string, string> = {
        'Green Screen': 'green_screen',
        'Office Desk': 'office_desk',
        'Interview setup with mic': 'interview',
        'Car interior': 'car',
        'Holding laptop': 'holding-laptop',
        'Coffee shop': 'coffee_shop',
        'Home Office': 'home_office',
        'any': 'any',
      };
      return bgMap[value] || value.toLowerCase().replace(/\s+/g, '_');
    }
    
    // Default: lowercase and replace spaces with underscores
    return value.toLowerCase().replace(/\s+/g, '_');
  };

  const handleFilterChange = (filterType: keyof FilterState, value: string | string[]) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    
    // Track screen-click event for filter selection
    if (filterType === 'hair') {
      // For hair, track each selected value
      const hairArray = value as string[];
      hairArray.forEach(hairValue => {
        const normalizedValue = normalizeValueForButton(filterType, hairValue);
        const buttonName: MixpanelScreenClickButton = `${filterType}-${normalizedValue}` as MixpanelScreenClickButton;
        mixpanelService.trackScreenClick(
          buttonName,
          rateLimitStatus?.remainingUses ?? 0,
          hasResults
        );
      });
    } else {
      const normalizedValue = normalizeValueForButton(filterType, value as string);
      const buttonName: MixpanelScreenClickButton = `${filterType}-${normalizedValue}` as MixpanelScreenClickButton;
      mixpanelService.trackScreenClick(
        buttonName,
        rateLimitStatus?.remainingUses ?? 0,
        hasResults
      );
    }
    
    onFiltersChange(newFilters);
  };

  const handleStyleChange = (newStyle: StyleState) => {
    const newFilters = { ...filters, style: newStyle };
    setFilters(newFilters);
    
    // Track screen-click events for style changes
    // Note: StyleDropdown may trigger multiple changes, so we track each change
    if (newStyle.type !== filters.style.type) {
      mixpanelService.trackScreenClick(
        `style-type-${newStyle.type}` as MixpanelScreenClickButton,
        rateLimitStatus?.remainingUses ?? 0,
        hasResults
      );
    }
    if (newStyle.ratio !== filters.style.ratio) {
      mixpanelService.trackScreenClick(
        `style-ratio-${newStyle.ratio}` as MixpanelScreenClickButton,
        rateLimitStatus?.remainingUses ?? 0,
        hasResults
      );
    }
    if (newStyle.scene !== filters.style.scene) {
      // Normalize scene value for button name
      const normalizedScene = normalizeValueForButton('scene', newStyle.scene);
      mixpanelService.trackScreenClick(
        `style-scene-${normalizedScene}` as MixpanelScreenClickButton,
        rateLimitStatus?.remainingUses ?? 0,
        hasResults
      );
    }
    
    onFiltersChange(newFilters);
  };

  const handleDropdownToggle = (dropdownType: string) => {
    const isOpening = openDropdown !== dropdownType;
    setOpenDropdown(openDropdown === dropdownType ? null : dropdownType);
    
    // Close other menus when dropdown is opened
    if (isOpening) {
      setShowSuggestions(false);
      setShowFileUpload(false);
      if (isHistoryVisible && onHistoryToggle) {
        onHistoryToggle();
      }
      
      // Track screen-click event for menu opening
      const buttonName: MixpanelScreenClickButton = `${dropdownType}-menu` as MixpanelScreenClickButton;
      mixpanelService.trackScreenClick(
        buttonName,
        rateLimitStatus?.remainingUses ?? 0,
        hasResults
      );
    }
  };

  const handleDropdownClose = () => {
    setOpenDropdown(null);
  };

  const handleSuggestionsToggle = () => {
    const newShowSuggestions = !showSuggestions;
    if (newShowSuggestions) {
      // Close other menus when opening suggestions
      setShowFileUpload(false);
      setOpenDropdown(null);
      if (isHistoryVisible && onHistoryToggle) {
        onHistoryToggle();
      }
    }
    
    // Track screen-click event
    mixpanelService.trackScreenClick(
      'suggestions',
      rateLimitStatus?.remainingUses ?? 0,
      hasResults
    );
    
    setShowSuggestions(newShowSuggestions);
  };

  const handleSuggestionsClose = () => {
    setShowSuggestions(false);
  };

  const handleSuggestionSelect = (suggestionPrompt: string, suggestionFilters: Partial<FilterState>) => {
    setPrompt(suggestionPrompt);
    onPromptChange(suggestionPrompt);
    
    // Merge suggestion filters with current filters
    const updatedFilters: FilterState = {
      gender: suggestionFilters.gender || filters.gender,
      age: suggestionFilters.age || filters.age,
      hair: suggestionFilters.hair ? stringToHairArray(suggestionFilters.hair) : filters.hair,
      background: suggestionFilters.background || filters.background,
      style: {
        type: suggestionFilters.style?.type || filters.style.type,
        ratio: suggestionFilters.style?.ratio || filters.style.ratio,
        scene: suggestionFilters.style?.scene || filters.style.scene
      }
    };
    
    // Track screen-click event for suggestion selection
    mixpanelService.trackScreenClick(
      'suggestion',
      rateLimitStatus?.remainingUses ?? 0,
      hasResults
    );
    
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleFileSelect = (file: File | null, base64: string | null) => {
    setUploadedFile(file);
    // Unused - kept for future use
    // setFileBase64(base64);
    
    if (file) {
      // Create preview URL
      const url = FileProcessor.createPreviewUrl(file);
      setPreviewUrl(url);
    } else {
      // Clean up preview URL
      if (previewUrl) {
        FileProcessor.revokePreviewUrl(previewUrl);
      }
      setPreviewUrl(null);
    }

    // Notify parent component
    if (onFileChange) {
      onFileChange(file, base64);
    }
  };

  const handleFileRemove = () => {
    handleFileSelect(null, null);
  };

  const handleAttachmentClick = () => {
    const newShowFileUpload = !showFileUpload;
    if (newShowFileUpload) {
      // Close other menus when opening file upload
      setShowSuggestions(false);
      setOpenDropdown(null);
      if (isHistoryVisible && onHistoryToggle) {
        onHistoryToggle();
      }
    }
    
    // Track screen-click event
    mixpanelService.trackScreenClick(
      'attach-image',
      rateLimitStatus?.remainingUses ?? 0,
      hasResults
    );
    
    setShowFileUpload(newShowFileUpload);
  };

  const handleGenerate = () => {
    // Check if generation is blocked
    if (isGenerating || disabled) {
      return;
    }
    
    // Show out of credits modal if rate limit is reached
    if (rateLimitStatus?.isBlocked) {
      openOutOfCreditsModal();
      return;
    }
    
    // Ensure prompt is not empty
    // if (!prompt.trim()) {
    //   return;
    // }
    
    // Track screen-click event for generate button
    mixpanelService.trackScreenClick(
      'generate',
      rateLimitStatus?.remainingUses ?? 0,
      hasResults
    );
    
    // Close all menus when starting generation
    closeAllMenus();
    
    // This would trigger the avatar generation
    if (onGenerate) {
      onGenerate();
    }
  };

  // Determine if generate button should be disabled
  const isGenerateDisabled = 
    isGenerating || 
    disabled || 
    !prompt.trim();

  // Get button text based on state
  const getGenerateButtonText = () => {
    if (isGenerating) return 'Generating...';
    if (rateLimitStatus?.isBlocked) return 'Get More Credits';
    if (!prompt.trim()) return 'Enter Prompt';
    return 'Generate';
  };

  return (
    <div className="w-full lg:max-w-[920px] mx-auto min-w-[290px]">
      {/* Main Input Container */}
      <div className="backdrop-blur-[20px] backdrop-filter bg-white box-border flex flex-col items-start justify-between p-4 relative rounded-2xl w-full min-h-[160px] lg:h-[180px]">
        <div aria-hidden="true" className="absolute border border-blue-black-30 border-solid inset-0 pointer-events-none rounded-2xl"/>
        
        {/* Text Input Area */}
        <div className="flex items-start justify-between relative shrink-0 w-full">
          <textarea
            value={prompt}
            onChange={handlePromptChange}
            placeholder="Define tone, style, and personality (e.g., friendly, professional, bold)."
            className="font-inter font-normal grow leading-[1.4] min-h-px min-w-px not-italic relative shrink-0 text-[16px] text-blue-black-90 bg-transparent border-none outline-none resize-none placeholder-blue-black-"
            rows={4}
            dir="auto" // Supports RTL languages
          />
        </div>

        {/* Filter Chips and Generate Button */}
        <div className="flex gap-2 items-end justify-start relative shrink-0 w-full">
          <div className="flex gap-2 grow items-center justify-between min-h-px min-w-px relative shrink-0">
            <div className="flex gap-2 items-center justify-start flex-wrap relative shrink-0">
              {/* Attachment Button */}
              <button
                onClick={handleAttachmentClick}
                className={`box-border flex gap-1 h-8 w-8 items-center justify-center p-1 relative rounded-lg shrink-0 transition-colors ${
                  showFileUpload || uploadedFile
                    ? 'bg-royal-30 border-royal' 
                    : 'border-royal-30 hover:bg-gray-50'
                }`}
                title={uploadedFile ? 'Image uploaded' : 'Upload image'}
              >
                <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                <div className="relative shrink-0 w-6 h-6">
                  <div className="absolute flex h-[22.627px] items-center justify-center left-[0.86px] top-px w-[22.627px]">
                    <div className="flex-none rotate-[45deg]">
                      <div className="h-5 relative w-3">
                        <div className="absolute inset-[-3.75%_-6.25%]" style={{ "--stroke-0": uploadedFile ? "rgba(59, 130, 246, 1)" : "rgba(37, 54, 88, 1)" } as React.CSSProperties}>
                          <img alt="" className="block max-w-none w-full h-full" src={img}/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {uploadedFile && (
                  <div className="absolute -top-1 -right-1 bg-green-500 rounded-full w-3 h-3 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                )}
              </button>

              {/* Suggestions Button */}
              <button
                onClick={handleSuggestionsToggle}
                className={`box-border flex gap-0.5 h-8 items-center justify-center relative rounded-lg shrink-0 transition-colors ${
                  showSuggestions 
                    ? 'bg-royal-30 border-royal' 
                    : 'border-royal-30 hover:bg-gray-50'
                } w-8 md:w-auto md:pl-2 md:pr-4 py-1 p-1 md:p-1`}
                title="Suggestions"
              >
                <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                <div className="overflow-clip relative shrink-0 w-6 h-6">
                  <div className="absolute h-[22.25px] left-[1.25px] top-[0.25px] w-[21.5px]">
                    <img alt="" className="block max-w-none w-full h-full" src={img1}/>
                  </div>
                </div>
                {/* Text label - hidden on mobile, shown on desktop */}
                <div className="hidden md:flex capitalize flex-col font-inter font-semibold justify-center leading-[0] not-italic relative shrink-0 text-blue-black-90 text-[14px] text-center text-nowrap">
                  <p className="leading-[normal] whitespace-pre">Suggestions</p>
                </div>
              </button>
              
              {/* Model Dropdown - with Brain Icon */}
              <DropdownMenu
                options={dropdownOptions.model}
                selectedValue={filters.model || 'flux-dev'}
                onSelect={(value) => handleFilterChange('model', value)}
                placeholder="Model"
                isOpen={openDropdown === 'model'}
                onToggle={() => handleDropdownToggle('model')}
                onClose={handleDropdownClose}
                icon={ASSETS.BRAIN_IMAGE}
              />
            </div>

            {/* Filter Controls - Responsive */}
            <div className="flex flex-wrap gap-2 items-center justify-start relative shrink-0">
              {/* Mobile Filter Button */}
              <div className="lg:hidden">
                <button
                  onClick={() => {
                    setShowMobileFilters(true);
                    // Close other menus
                    closeAllMenus();
                  }}
                  className="box-border flex gap-0.5 h-8 items-center justify-center px-3 py-0.5 relative rounded-lg shrink-0 border-royal-30 hover:bg-gray-50 transition-colors"
                >
                  <div aria-hidden="true" className="absolute border border-royal-30 border-solid inset-0 pointer-events-none rounded-lg"/>
                  <span className="capitalize font-inter font-semibold text-blue-black-90 text-[14px] text-center text-nowrap">
                    Appearance
                  </span>
                </button>
              </div>

              {/* Desktop Filter Dropdowns */}
              <div className="hidden lg:flex flex-wrap gap-2 items-center justify-start">
                <StyleDropdown
                  selectedStyle={filters.style}
                  onStyleChange={handleStyleChange}
                  isOpen={openDropdown === 'style'}
                  onToggle={() => handleDropdownToggle('style')}
                  onClose={handleDropdownClose}
                />
                <DropdownMenu
                  options={dropdownOptions.age}
                  selectedValue={filters.age}
                  onSelect={(value) => handleFilterChange('age', value)}
                  placeholder="Age"
                  isOpen={openDropdown === 'age'}
                  onToggle={() => handleDropdownToggle('age')}
                  onClose={handleDropdownClose}
                />
                <DropdownMenu
                  options={dropdownOptions.gender}
                  selectedValue={filters.gender}
                  onSelect={(value) => handleFilterChange('gender', value)}
                  placeholder="Gender"
                  isOpen={openDropdown === 'gender'}
                  onToggle={() => handleDropdownToggle('gender')}
                  onClose={handleDropdownClose}
                />
                <MultiSelectDropdown
                  options={dropdownOptions.hair}
                  selectedValues={filters.hair}
                  onSelect={(values) => handleFilterChange('hair', values)}
                  placeholder="Hair"
                  isOpen={openDropdown === 'hair'}
                  onToggle={() => handleDropdownToggle('hair')}
                  onClose={handleDropdownClose}
                />
                <DropdownMenu
                  options={dropdownOptions.background}
                  selectedValue={filters.background}
                  onSelect={(value) => handleFilterChange('background', value)}
                  placeholder="Background"
                  isOpen={openDropdown === 'background'}
                  onToggle={() => handleDropdownToggle('background')}
                  onClose={handleDropdownClose}
                />
                
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="relative group">
            <button
              onClick={handleGenerate}
              disabled={isGenerateDisabled}
              className={`
                box-border flex gap-1 items-center justify-center p-1 relative rounded-lg shrink-0 w-8 h-8 transition-all duration-200
                ${isGenerateDisabled 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-royal cursor-pointer hover:bg-blue-600'
                }
              `}
              title={getGenerateButtonText()}
            >
              <div className="relative shrink-0 w-6 h-6">
                {isGenerating ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mt-1"></div>
                ) : (
                  <div className="absolute h-[15.5px] left-[6.25px] top-[4.25px] w-[11.5px]">
                    <img 
                      alt={getGenerateButtonText()} 
                      className={`block max-w-none w-full h-full ${isGenerateDisabled ? 'opacity-50' : ''}`} 
                      src={img2}
                    />
                  </div>
                )}
              </div>
            </button>
            
            {/* Tooltip for disabled states */}
            {isGenerateDisabled && (
              <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {getGenerateButtonText()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Suggestions Section - Below Input Field */}
      {showSuggestions && (
        <div className="mt-4">
          <SuggestionsMenu
            isOpen={showSuggestions}
            onClose={handleSuggestionsClose}
            onSelectSuggestion={handleSuggestionSelect}
          />
        </div>
      )}

      {/* File Upload Section - Below Input Field */}
      {showFileUpload && (
        <div className="mt-4">
          <div className="backdrop-blur-[20px] backdrop-filter bg-white box-border p-4 relative rounded-2xl">
            <div aria-hidden="true" className="absolute border border-blue-black-30 border-solid inset-0 pointer-events-none rounded-2xl"/>
            <div className="mb-2">
              <h3 className="font-inter font-semibold text-blue-black-90 text-sm mb-1">Upload Reference Image</h3>
              <p className="font-inter text-blue-black-60 text-xs">
                Upload an image to use as a reference for image-to-image generation. The AI will use your image as a reference and apply your prompt and filters.
              </p>
            </div>
            <FileUpload
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              uploadedFile={uploadedFile}
              previewUrl={previewUrl}
              accept="image/jpeg,image/png,image/webp"
              maxSize={10 * 1024 * 1024} // 10MB
            />
          </div>
        </div>
      )}


      {/* Mobile Filter Modal */}
      <MobileFilterModal
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
          onFiltersChange(newFilters);
        }}
      />

      {/* Out of Credits Modal */}
      <OutOfCreditsModal
        isOpen={isOutOfCreditsOpen}
        onClose={closeOutOfCreditsModal}
        onBuyCredits={handleBuyCredits}
        title="You're out of credits!"
        subtitle="You've reached your weekly limit of 15 avatar generations. Subscribe to get unlimited access and create as many avatars as you want."
        isMobileView={isMobileView}
      />
    </div>
  );
}
